import React, { useEffect } from 'react';
import './App.css';
import { BookingProvider } from './context/BookingContext';
import { AuthProvider } from './context/AuthContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { useBooking } from './hooks/useBooking';
import useGoogleMaps from './hooks/useGoogleMaps';
import HomePage from './pages/HomePage';
import SearchResults from './pages/SearchResults';
import ExtrasPage from './pages/ExtrasPage';
import PassengerPage from './pages/PassengerPage';
import PaymentPage from './pages/PaymentPage';
import ConfirmationPage from './pages/ConfirmationPage';
import ManageBookingPage from './pages/ManageBookingPage';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailed from './pages/PaymentFailed';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ProfilePage from './pages/auth/ProfilePage';
import ProfileSettingsPage from './pages/auth/ProfileSettingsPage';
import BookingHistoryPage from './pages/auth/BookingHistoryPage';
import EmailVerificationPage from './pages/auth/EmailVerificationPage';
import AuthCallbackPage from './pages/auth/AuthCallbackPage';
import FAQPage from './pages/FAQPage';
import ContactPage from './pages/ContactPage';
import LoadingSpinner from './components/common/LoadingSpinner';
import { PAGES } from './utils/constants';

const AppContent = () => {
  const { state: { currentPage, loading }, setCurrentPage, setManageBookingId } = useBooking();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const currentPath = window.location.pathname;
    
    // ✅ ADD THIS: Check for email verification page
    if (currentPath === '/verify-email' && currentPage !== PAGES.EMAIL_VERIFICATION) {
      setCurrentPage(PAGES.EMAIL_VERIFICATION);
      return;
    }

    // Check for auth callback page
    if (currentPath === '/auth-callback' && currentPage !== PAGES.AUTH_CALLBACK) {
      setCurrentPage(PAGES.AUTH_CALLBACK);
      return;
    }

    // Check for WebXPay success page
    if (currentPath === '/payment-success' && currentPage !== PAGES.PAYMENT_SUCCESS) {
      setCurrentPage(PAGES.PAYMENT_SUCCESS);
      return;
    }
    
    // Check for WebXPay failure page  
    if (currentPath === '/payment-failed' && currentPage !== PAGES.PAYMENT_FAILED) {
      setCurrentPage(PAGES.PAYMENT_FAILED);
      return;
    }
    
    // Check for FAQ page
    if (currentPath === '/faq' && currentPage !== PAGES.FAQ) {
      setCurrentPage(PAGES.FAQ);
      return;
    }

    // Check for Contact page
    if (currentPath === '/contact' || currentPath === '/contact/') {
      if (currentPage !== PAGES.CONTACT) {
        setCurrentPage(PAGES.CONTACT);
      }
      return;
    }

    // Existing manage booking logic
    const manageId = urlParams.get('manage') || localStorage.getItem('manageBookingId');
    if (manageId && currentPage !== PAGES.MANAGE_BOOKING) {
      setManageBookingId(manageId);
      setCurrentPage(PAGES.MANAGE_BOOKING);
      localStorage.removeItem('manageBookingId');

      // Clear query parameter from the browser URL so header navigation works properly
      if (urlParams.has('manage')) {
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [setCurrentPage, setManageBookingId, currentPage]);

  const { isLoaded: googleMapsLoaded, loadError } = useGoogleMaps(
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  );

  // Show error if Google Maps failed to load
  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-gray-700 mb-2">{loadError}</p>
          <p className="text-sm text-gray-500">Please check your Google Maps API key in .env file</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show loading while Google Maps is loading
  if (!googleMapsLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner message="Loading maps..." size="large" />
      </div>
    );
  }

  // Show loading during search
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner message="Searching for the best options..." size="large" />
      </div>
    );
  }

  const renderCurrentPage = () => {
    switch (currentPage) {
      case PAGES.HOME:
        return <HomePage />;
      case PAGES.SEARCH:
        return <SearchResults />;
      case PAGES.EXTRAS:
        return <ExtrasPage />;
      case PAGES.PASSENGER:
        return <PassengerPage />;
      case PAGES.PAYMENT:
        return <PaymentPage />;
      case PAGES.CONFIRMATION:
        return <ConfirmationPage />;
      case PAGES.MANAGE_BOOKING: 
        return <ManageBookingPage />;
      case PAGES.PAYMENT_SUCCESS:
        return <PaymentSuccess />;
      case PAGES.PAYMENT_FAILED:
        return <PaymentFailed />;
      case PAGES.LOGIN:
        return <LoginPage />;
      case PAGES.REGISTER:
        return <RegisterPage />;
      case PAGES.PROFILE:
        return <ProfilePage />;
      case PAGES.PROFILE_SETTINGS:
        return <ProfileSettingsPage />;
      case PAGES.BOOKING_HISTORY:
        return <BookingHistoryPage />;
      case PAGES.EMAIL_VERIFICATION:
        return <EmailVerificationPage />;
      case PAGES.AUTH_CALLBACK:
        return <AuthCallbackPage />;
      case PAGES.FAQ:
        return <FAQPage />;
      case PAGES.CONTACT:
        return <ContactPage />;
      default:
        return <HomePage />;
    }
  };

  return renderCurrentPage();
};

function App() {
  return (
    <AuthProvider>
      <BookingProvider>
        <CurrencyProvider>
          <div className="App">
            <AppContent />
          </div>
        </CurrencyProvider>
      </BookingProvider>
    </AuthProvider>
  );
}

export default App;